import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import * as ImageManipulator from 'expo-image-manipulator';

/**
 * Shrink a picked picture to something EVERY device can actually upload.
 *
 * WHY THIS EXISTS — the Android hole in the upload ladder.
 *
 * `services/workOrderPhotoApi` sends a photo over three transports: a native
 * multipart stream first, a base64 JSON envelope as the fallback for the
 * Cloudflare proxy chain that can answer an opaque `502 origin_bad_gateway` to
 * a `multipart/form-data` POST, and browser multipart on web. The JSON fallback
 * is gated at 700 KB, because it materializes the picture several times over in
 * the JS heap and an unbounded one is what the iOS memory watchdog used to kill
 * the app for.
 *
 * Nothing in that chain ever RESIZED the picture — both platforms only
 * re-encode — and the two platforms do not produce remotely comparable files:
 *
 *  - iOS asks PhotoKit for the compatible asset representation (see
 *    `helpers/imagePickerOptions`), so a 12 MP photo comes back as a JPEG that
 *    honours `quality`.
 *  - Android runs expo-image-picker's `CompressionImageExporter`, which decodes
 *    the asset at FULL sensor resolution and re-compresses it. A 50–108 MP
 *    phone therefore hands back several megabytes at the very same `quality`.
 *
 * So on Android the multipart body is several times larger — far more likely to
 * die in the proxy chain or to run past OkHttp's 60 s write timeout on a mobile
 * network — and then, being well over 700 KB, it is ALSO too big for the JSON
 * fallback that exists precisely to rescue it. The upload simply failed, while
 * the identical code path worked from an iPhone.
 *
 * Capping the longest edge before the picture ever leaves the phone fixes all of
 * it at once: the stream upload is small and fast, the fallback is reachable
 * again, and the bytes are always a real JPEG/PNG rather than whatever container
 * the gallery happened to hold (expo-image-picker's Android exporter keeps a
 * `.webp`/`.gif`/`.bmp` extension, which the client then had to declare
 * dishonestly as `image/jpeg`).
 *
 * FAIL-OPEN, always. A device whose decoder cannot read the source — an old
 * Android without HEIF support, say — keeps the original asset and the previous
 * behaviour (honest type + the server's relabelled-as-JPEG retry); a picture
 * that is merely large beats no picture at all.
 *
 * DEVICE ONLY. Web and the Tauri desktop build upload through the browser's own
 * multipart transport, which is what the admin panel has always used for
 * full-size documents and which none of the above applies to; they also hold the
 * picked file as a `File`/`Blob` the render would have to replace. Leaving that
 * path byte-identical keeps the fix to the platforms that actually needed it.
 */

/**
 * Longest edge of the uploaded picture. 2000 px still reads a serial plate or a
 * scratch at full zoom, and lands a normal photo around 300–600 KB.
 * @type {number}
 */
const MAX_EDGE = 2000;

/**
 * Longest edge of the second pass, used only when the first one stays over
 * `TARGET_BYTES` (a very detailed scene at 2000 px can).
 * @type {number}
 */
const RETRY_MAX_EDGE = 1400;

/** JPEG quality of the first pass. */
const QUALITY = 0.7;

/** JPEG quality of the second pass. */
const RETRY_QUALITY = 0.6;

/**
 * Size the result is aimed at — the same 700 KB the base64 JSON fallback is
 * gated on, so a prepared photo can always take that transport when the
 * multipart stream dies in the proxy chain.
 * @type {number}
 */
const TARGET_BYTES = 700 * 1024;

/**
 * Is the picked asset a PNG?
 *
 * PNG is kept as PNG: it is the format screenshots and anything with
 * transparency arrive in, and re-encoding those to JPEG would flatten the alpha
 * channel to black. The resize alone already shrinks them dramatically.
 *
 * @param {{fileName?: string, mimeType?: string, uri?: string}} asset Picked asset.
 * @returns {boolean}
 */
const isPngAsset = (asset) => {
    const declared = String(asset?.mimeType || '').toLowerCase();

    if (declared) {
        return declared === 'image/png';
    }

    return /\.png(?:\?|$)/i.test(String(asset?.fileName || asset?.uri || ''));
};

/**
 * Proportional resize action for the picked asset, or nothing when the picture
 * is already small enough.
 *
 * Only ONE edge is given, so expo-image-manipulator keeps the aspect ratio; the
 * longer one is chosen from the picker's own dimensions. When those are missing
 * the picture is re-encoded but NOT resized — passing a width blind would
 * upscale a small image and make the file bigger, which is the opposite of the
 * point.
 *
 * @param {{width?: number, height?: number}} asset Picked asset.
 * @param {number} maxEdge Longest edge to allow.
 * @returns {Array<Object>} `manipulateAsync` actions (empty = re-encode only).
 */
const resizeActions = (asset, maxEdge) => {
    const width = Number(asset?.width) || 0;
    const height = Number(asset?.height) || 0;

    if (!width || !height || Math.max(width, height) <= maxEdge) {
        return [];
    }

    return [{ resize: width >= height ? { width: maxEdge } : { height: maxEdge } }];
};

/**
 * On-disk size of a local file, or 0 when it cannot be measured (web, or a URI
 * `expo-file-system` does not own).
 *
 * @param {string} uri File URI.
 * @returns {Promise<number>} Size in bytes.
 */
const fileSizeOf = async (uri) => {
    if (typeof FileSystem.getInfoAsync !== 'function') {
        return 0;
    }

    try {
        const { size = 0 } = await FileSystem.getInfoAsync(uri, { size: true }) || {};

        return Number(size) || 0;
    } catch (_error) {
        return 0;
    }
};

/**
 * Delete a temporary file the manipulator produced, best effort.
 *
 * @param {?string} uri File URI to drop.
 * @returns {void}
 */
const dropTempFile = (uri) => {
    if (typeof uri !== 'string' || !uri.startsWith('file://')) {
        return;
    }
    if (typeof FileSystem.deleteAsync !== 'function') {
        return;
    }

    FileSystem.deleteAsync(uri, { idempotent: true }).catch(err => {
        console.warn('preparePhotoForUpload: dropping a temp render failed', err?.message);
    });
};

/**
 * Run one manipulator pass.
 *
 * @param {{uri: string}} asset Picked asset.
 * @param {{maxEdge: number, quality: number, png: boolean}} pass Pass settings.
 * @returns {Promise<{uri: string, width?: number, height?: number}>} Render result.
 */
const renderPass = async (asset, { maxEdge, quality, png }) => ImageManipulator.manipulateAsync(
    asset.uri,
    resizeActions(asset, maxEdge),
    {
        compress: quality,
        format: png ? ImageManipulator.SaveFormat.PNG : ImageManipulator.SaveFormat.JPEG,
    },
);

/**
 * Produce an upload-sized copy of a picked picture.
 *
 * The returned asset is a drop-in replacement for the picker's own: same shape,
 * but pointing at the rendered copy and declaring its real type. `isDerived`
 * marks the copy so the caller can delete it once the upload is over —
 * `discardPreparedPhoto` does exactly that.
 *
 * Never throws: any failure returns the ORIGINAL asset untouched.
 *
 * @param {{uri?: string, width?: number, height?: number, fileName?: string,
 *   mimeType?: string, file?: Object, base64?: string}} asset
 *   expo-image-picker asset.
 * @returns {Promise<Object>} Prepared asset, or the original one.
 */
export const preparePhotoForUpload = async (asset) => {
    if (Platform.OS === 'web' || !asset?.uri || typeof ImageManipulator?.manipulateAsync !== 'function') {
        return asset;
    }

    const png = isPngAsset(asset);

    try {
        let result = await renderPass(asset, { maxEdge: MAX_EDGE, quality: QUALITY, png });
        let size = await fileSizeOf(result.uri);

        // A very detailed scene can still clear the target at 2000 px. Render the
        // second pass from the ORIGINAL rather than from the first result, so the
        // picture is compressed once rather than twice over.
        if (size > TARGET_BYTES) {
            const retry = await renderPass(asset, {
                maxEdge: RETRY_MAX_EDGE,
                quality: RETRY_QUALITY,
                png,
            });
            const retrySize = await fileSizeOf(retry.uri);

            if (!retrySize || retrySize < size) {
                dropTempFile(result.uri);
                result = retry;
                size = retrySize;
            } else {
                dropTempFile(retry.uri);
            }
        }

        return {
            ...asset,
            uri: result.uri,
            width: result.width || asset.width,
            height: result.height || asset.height,
            mimeType: png ? 'image/png' : 'image/jpeg',
            // Both describe the ORIGINAL picture, which no longer exists at this
            // URI: a stale `fileName` would put the wrong extension on the upload
            // and a stale `file`/`base64` would send the wrong bytes on web.
            fileName: null,
            file: null,
            base64: null,
            size: size || undefined,
            // Only a render that produced its OWN file may be deleted afterwards
            // — never the picker's copy, which the screen still owns.
            isDerived: result.uri !== asset.uri,
        };
    } catch (error) {
        // An unreadable source (old Android decoder, exotic container) must not
        // cost the technician the picture — fall back to the untouched asset and
        // let the existing transport ladder try it as-is.
        console.warn('preparePhotoForUpload: falling back to the original asset', error?.message);

        return asset;
    }
};

/**
 * Delete the temporary copy `preparePhotoForUpload` rendered, if it made one.
 *
 * The manipulator writes into the app's cache directory exactly like the picker
 * does, so leaving the renders behind would grow the app's disk use on every
 * single upload.
 *
 * @param {?{uri?: string, isDerived?: boolean}} prepared Prepared asset.
 * @returns {void}
 */
export const discardPreparedPhoto = (prepared) => {
    if (!prepared?.isDerived) {
        return;
    }

    const uri = prepared.uri;

    // Web/Tauri renders come back as an object URL the browser owns; releasing it
    // is the equivalent of deleting the native temp file. Reached only if this
    // helper is ever let onto the web path — `globalThis` rather than a bare
    // `URL`, which React Native only polyfills on some platforms.
    if (typeof uri === 'string' && uri.startsWith('blob:')) {
        if (typeof globalThis?.URL?.revokeObjectURL === 'function') {
            globalThis.URL.revokeObjectURL(uri);
        }

        return;
    }

    dropTempFile(uri);
};

export default { preparePhotoForUpload, discardPreparedPhoto };
